import { MAX_NOTE_ATTACHMENT_COUNT } from "../../../constants/noteAttachment.constants";
import { getAttachmentFileKey } from "../../../utils/noteAttachment.util";
import {
  DEFAULT_PANEL_BORDER_CLASS,
  FILE_SIZE_BASE,
  FILE_SIZE_UNITS,
  HIGHLIGHTED_PANEL_BORDER_CLASS,
  PANEL_BASE_CLASS,
  READY_TO_UPLOAD_TEXT,
  REMOVAL_PENDING_TEXT,
  SAVED_FILE_TEXT,
} from "./attachmentPanel.constants";

export const ATTACHMENT_ROW_KINDS = Object.freeze({
  SAVED: "saved",
  SELECTED: "selected",
});

export const getPanelClassName = (isHighlighted) =>
  `${PANEL_BASE_CLASS} ${
    isHighlighted ? HIGHLIGHTED_PANEL_BORDER_CLASS : DEFAULT_PANEL_BORDER_CLASS
  }`;

const formatFileSize = (size = 0) => {
  if (!size) return "0 B";

  const unitIndex = Math.min(
    Math.floor(Math.log(size) / Math.log(FILE_SIZE_BASE)),
    FILE_SIZE_UNITS.length - 1
  );
  const normalizedSize = size / FILE_SIZE_BASE ** unitIndex;
  return `${normalizedSize.toFixed(unitIndex ? 1 : 0)} ${FILE_SIZE_UNITS[unitIndex]}`;
};

const buildSavedAttachmentRow = ({ attachment, pendingRemovalBlobNames }) => {
  const isRemovalPending = pendingRemovalBlobNames.includes(attachment.blobName);
  const statusText = isRemovalPending ? REMOVAL_PENDING_TEXT : SAVED_FILE_TEXT;

  return {
    attachment,
    blobName: attachment.blobName,
    canDownload: Boolean(attachment.downloadUrl) && !isRemovalPending,
    isRemovalPending,
    key: `saved:${attachment.blobName}`,
    kind: ATTACHMENT_ROW_KINDS.SAVED,
    metaText: `${statusText} · ${formatFileSize(attachment.size)}`,
    name: attachment.fileName || "Attachment",
  };
};

const buildSelectedFileRow = (file) => ({
  canDownload: false,
  fileKey: getAttachmentFileKey(file),
  isRemovalPending: false,
  key: `selected:${getAttachmentFileKey(file)}`,
  kind: ATTACHMENT_ROW_KINDS.SELECTED,
  metaText: `${READY_TO_UPLOAD_TEXT} · ${formatFileSize(file.size)}`,
  name: file.name,
});

const buildAttachmentRows = ({
  pendingRemovalBlobNames,
  savedAttachments,
  selectedFiles,
}) => [
  ...savedAttachments.map((attachment) =>
    buildSavedAttachmentRow({ attachment, pendingRemovalBlobNames })
  ),
  ...selectedFiles.map(buildSelectedFileRow),
];

const countAttachedFiles = (attachmentRows) =>
  attachmentRows.filter((row) => !row.isRemovalPending).length;

/**
 * Derives the render model for the attachment panel: one row per saved or
 * selected file plus the header count and upload-button availability.
 * @param {{
 *   isDisabled: boolean,
 *   pendingRemovalBlobNames: string[],
 *   savedAttachments: object[],
 *   selectedFiles: File[]
 * }} panelInput
 * @returns {object}
 */
export const getAttachmentPanelState = ({
  isDisabled,
  pendingRemovalBlobNames,
  savedAttachments,
  selectedFiles,
}) => {
  const attachmentRows = buildAttachmentRows({
    pendingRemovalBlobNames,
    savedAttachments,
    selectedFiles,
  });
  const attachedCount = countAttachedFiles(attachmentRows);

  return {
    attachedCountText: attachedCount
      ? `${attachedCount} of ${MAX_NOTE_ATTACHMENT_COUNT} attached`
      : "",
    attachmentRows,
    hasAttachments: attachmentRows.length > 0,
    isUploadDisabled: isDisabled || attachedCount >= MAX_NOTE_ATTACHMENT_COUNT,
  };
};
